import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Mail, ArrowRight, Fingerprint, Eye, EyeOff } from 'lucide-react';
import Logo from '../components/Logo';

const Login: React.FC = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        alert('Verification link sent. Please check your email to confirm.');
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate('/');
      }
    } catch (err: any) {
      setError("Login failed. Please check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fafc]">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <Logo size={110} className="mx-auto mb-6 drop-shadow-2xl" />
          <h1 className="text-4xl font-extrabold text-[#0f172a] tracking-tight text-center">E&T SecureTrack</h1>
          <div className="flex items-center justify-center mt-4 space-x-3">
             <span className="h-px w-8 bg-slate-200"></span>
             <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em]">Personal Expense Tracker</p>
             <span className="h-px w-8 bg-slate-200"></span>
          </div>
        </div>

        <div className="bg-white p-10 rounded-[3.5rem] shadow-[0_40px_80px_-15px_rgba(0,0,0,0.08)] border border-slate-50">
          <div className="flex bg-slate-50 p-1.5 rounded-3xl mb-10 border border-slate-100">
            <button 
              type="button"
              className={`flex-1 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${!isSignUp ? 'bg-[#0f172a] text-white shadow-xl translate-y-[-1px]' : 'text-slate-400'}`} 
              onClick={() => setIsSignUp(false)}
            >
              Sign In
            </button>
            <button 
              type="button"
              className={`flex-1 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${isSignUp ? 'bg-[#0f172a] text-white shadow-xl translate-y-[-1px]' : 'text-slate-400'}`} 
              onClick={() => setIsSignUp(true)}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleAuth} className="space-y-7">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Email</label>
              <div className="relative group">
                <Mail className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f172a] transition-colors w-5 h-5" />
                <input type="email" required className="w-full pl-16 pr-6 py-5 bg-slate-50 border border-transparent rounded-[2rem] focus:bg-white focus:border-slate-200 focus:ring-4 focus:ring-slate-50 outline-none text-slate-900 font-bold transition-all" placeholder="Enter secure email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3 ml-1">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Password</label>
              </div>
              <div className="relative group">
                <Fingerprint className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f172a] transition-colors w-5 h-5" />
                <input 
                  type={showPassword ? "text" : "password"} 
                  required 
                  className="w-full pl-16 pr-16 py-5 bg-slate-50 border border-transparent rounded-[2rem] focus:bg-white focus:border-slate-200 focus:ring-4 focus:ring-slate-50 outline-none text-slate-900 font-bold transition-all" 
                  placeholder="••••••••" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="flex justify-end mt-3 mr-1">
                <button 
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-[9px] font-black text-amber-500 hover:text-amber-600 uppercase tracking-widest transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && <p className="text-red-500 text-[10px] font-black uppercase text-center bg-red-50 p-4 rounded-2xl border border-red-100 tracking-wider">{error}</p>}

            <button type="submit" disabled={loading} className="w-full bg-[#0f172a] text-[#fbbf24] py-5 rounded-[2.5rem] font-black shadow-2xl shadow-slate-200 flex items-center justify-center hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 mt-4 group border border-white/5">
              {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <>{isSignUp ? 'Create Account' : 'Sign In'} <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
